import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { getCheckoutStatus } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

export default function Success() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: "polling", data: null, attempts: 0 });

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState({ status: "error", data: null, attempts: 0 });
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;

        const paid = data.payment_status === "paid";
        const failed = ["failed", "expired"].includes(data.payment_status) || data.status === "expired";

        if (paid) {
          setState({ status: "paid", data, attempts });
          return;
        }
        if (failed) {
          setState({ status: "expired", data, attempts });
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", data, attempts });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        if (!cancelled) setState({ status: "error", data: null, attempts });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [params]);

  return (
    <main data-testid="success-page" className="min-h-screen luxe-bg flex items-center justify-center px-6">
      <div className="max-w-xl w-full text-center fade-up">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-[#d89557] mx-auto animate-spin mb-6" />
            <h1 className="font-display text-3xl sm:text-4xl text-[#f4ede0] mb-3">{t("success.verifying")}</h1>
            <p className="text-[#cbc1ad] text-sm">{t("success.pleaseWait")}</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-[#d89557] mx-auto mb-6" />
            <h1 data-testid="payment-success-title" className="font-display text-4xl sm:text-5xl text-[#f4ede0] mb-4">
              {t("success.thanks1")} <span className="copper-text italic">{t("success.thanks2")}</span>.
            </h1>
            <p className="text-[#cbc1ad] mb-2">{t("success.saved")}</p>
            {state.data?.amount_total != null && (
              <p className="font-display text-3xl copper-text mb-8">
                {(state.data.amount_total / 100).toFixed(2)} CHF
              </p>
            )}
            <p className="text-xs text-[#7a716a] uppercase tracking-[0.25em] mb-8">{t("success.emailSent")}</p>
            <Link to="/" className="btn-ghost-copper inline-flex items-center gap-3 px-6 py-3 text-sm uppercase tracking-[0.2em]">
              <ArrowLeft className="w-4 h-4" /> {t("success.backHome")}
            </Link>
          </>
        )}
        {(state.status === "error" || state.status === "expired" || state.status === "timeout") && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h1 className="font-display text-3xl sm:text-4xl text-[#f4ede0] mb-4">
              {state.status === "expired" ? t("success.expired") : state.status === "timeout" ? t("success.timeout") : t("success.errorTitle")}
            </h1>
            <p className="text-[#cbc1ad] mb-8 text-sm">{t("success.errorDesc")}</p>
            <Link to="/" className="btn-copper inline-flex items-center gap-3 px-6 py-3 text-sm uppercase tracking-[0.2em]">
              {t("success.backHome")}
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
